/**
 * Credit Exhaustion Modal Component
 *
 * Modal dialog that appears when a free plan user has exhausted their monthly credits.
 * Displays credit status, missing features, and provides upgrade option.
 *
 * @module @ideoz/ui/components/molecules/CreditExhaustionModal
 */

import React from 'react';
import { X, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from '../atoms/dialog';
import { Button } from '../atoms/button';
import { cn } from '@ideoz/ui';
import type { CreditExhaustionModalProps } from '../../types/CreditExhaustionModal.types';

/**
 * Smart plan benefits list
 * Features that free plan users are missing
 */
const SMART_PLAN_BENEFITS = [
  'Higher credit limits with Smart plan',
  "Plan's credits roll over to next month (for paid plans)",
  'Unlimited projects',
  'Multiple prototypes with iterative flows',
  'Update prototype design system',
  'Advanced context builder for richer designs',
];

/**
 * CreditExhaustionModal Component
 *
 * Displays a modal when free plan users have exhausted their credits.
 * Shows credit status, missing features, and upgrade option.
 *
 * @component
 * @example
 * ```tsx
 * <CreditExhaustionModal
 *   isOpen={true}
 *   onClose={() => setIsOpen(false)}
 *   availableCredits={0}
 *   totalCredits={8}
 *   onUpgrade={() => handleUpgrade()}
 * />
 * ```
 */
export const CreditExhaustionModal: React.FC<CreditExhaustionModalProps> = ({
  isOpen,
  onClose,
  availableCredits,
  totalCredits,
  onUpgrade,
  isLoading = false,
  className = '',
  testId = 'credit-exhaustion-modal',
}) => {
  const remainingCredits = totalCredits - availableCredits;
  const isExhausted = availableCredits === 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={cn(
          'max-w-lg w-full bg-white rounded-2xl p-6',
          className
        )}
        data-testid={testId}
      >
        {/* Header with Close Button */}
        <DialogHeader className="relative">
          <DialogTitle className="text-xl font-semibold text-[#252B37] pr-8">
            You've used all your monthly credits
          </DialogTitle>
          <DialogClose
            onClick={onClose}
            className="absolute top-0 right-0 inline-flex h-6 w-6 items-center justify-center rounded-md text-[#717680] transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4B579F]"
            aria-label="Close"
            data-testid={`${testId}-close-button`}
          >
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>

        {/* Description */}
        <DialogDescription className="text-sm text-[#717680] mt-2">
          You've reached your credit limit for this month. Upgrade your plan or purchase additional credits to continue.
        </DialogDescription>

        {/* Credit Status Display */}
        <div className="mt-6 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-[#252B37]">Available credits</p>
            <p className="text-sm font-semibold text-[#252B37]">
              {availableCredits}/{totalCredits}
            </p>
          </div>
          <div className="w-full bg-[#E9EAEB] rounded-full h-2 overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full transition-all duration-300',
                isExhausted ? 'bg-gray-300' : 'bg-blue-light-600'
              )}
              style={{ width: `${(remainingCredits / totalCredits) * 100}%` }}
              data-testid={`${testId}-credit-bar`}
            />
          </div>
        </div>

        {/* Benefits Checklist */}
        <div className="mt-6 space-y-3">
          <p className="text-sm font-semibold text-[#252B37] mb-2">
            What you're missing with Smart plan:
          </p>
          <div className="space-y-2.5">
            {SMART_PLAN_BENEFITS.map((benefit, index) => (
              <div
                key={index}
                className="flex items-start gap-2"
                data-testid={`${testId}-benefit-${index}`}
              >
                <Check
                  size={18}
                  className="text-success-500 mt-0.5 flex-shrink-0"
                  strokeWidth={2.5}
                />
                <span className="text-sm text-[#717680] leading-relaxed">
                  {benefit}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Upgrade Button */}
        {onUpgrade && (
        <div className="mt-6 flex justify-end">
          <Button
            onClick={async () => {
              if (onUpgrade) {
                await onUpgrade();
              }
            }}
            disabled={isLoading || !onUpgrade}
            className="w-full sm:w-auto bg-[#333A63] hover:bg-[#2d3461] text-white rounded-lg font-semibold h-10 px-6 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid={`${testId}-upgrade-button`}
          >
            {isLoading ? 'Processing...' : 'Upgrade'}
          </Button>
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

CreditExhaustionModal.displayName = 'CreditExhaustionModal';

export default CreditExhaustionModal;
